import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import MovableBar from './MovableBar';
import TaskInfoPanel from './TaskInfoPanel';
import SchuelerModal from './SchuelerModal';
import { useEditing } from '../EditingContext';
import { useStartDate } from '../StartDateContext';
import { useEditBarDialog } from '../EditBarDialogContext';
import { useCalendarStrips } from '../CalendarStripsContext';
import { useCopyPaste } from '../CopyPasteContext';
import { useHasChanges } from '../HasChangesContext';
import { usePlanMetadata } from '../PlanMetadataContext';
import { usePlanName } from '../PlanNameContext';
import { useConfirmAlert } from '../hooks/useConfirmAlert';
import { updateTaskAssignments, addTaskAssignments, fetchTaskAssignments, copyTaskAssignmentsToNewUUID } from '../api';
import { FiPlus } from 'react-icons/fi';

// Funktion zur Generierung einer eindeutigen UUID
const generateUUID = () => {
  return 'task-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 18);
};

const Task = forwardRef(({ visibleWeeks = 10, weekWidth = 200, isVisibleWeeksChanging = false, isZoomChanging = false, initialBars = [], onBarsChange, onInfoPanelsChange, batchOperations, panelId, title, groupTitle }, ref) => {
  // Eindeutige Task-ID generieren
  const taskIdRef = React.useRef(`task-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`);
  
  // Convert string dates to Date objects immediately when component receives initialBars
  // UUID NICHT automatisch generieren beim initialen Laden - nur bestehende UUIDs übernehmen
  const convertedInitialBars = initialBars.map(bar => {
    // IDs beibehalten: numerische IDs bleiben Zahlen, nicht-numerische Strings (z.B. aus Batch-Move)
    // bleiben erhalten, damit sie eindeutig bleiben und nicht alle auf Date.now() fallen.
    const barId = typeof bar.id === 'number' ? bar.id : (parseInt(bar.id, 10) || bar.id);
    
    return {
      ...bar,
      id: barId,
      uuid: bar.uuid, // Bestehende UUID beibehalten, keine neue generieren
      startDate: bar.startDate ? new Date(bar.startDate) : undefined,
      endDate: bar.endDate ? new Date(bar.endDate) : undefined
    };
  });

  const [bars, setBars] = useState(convertedInitialBars);
  const [nextBarId, setNextBarId] = useState(() => {
    if (convertedInitialBars.length > 0) {
      const validIds = convertedInitialBars
        .map(bar => typeof bar.id === 'number' ? bar.id : parseInt(bar.id, 10))
        .filter(id => !isNaN(id) && id > 0);
      const maxId = validIds.length > 0 ? Math.max(...validIds) : 0;
      return maxId + 1;
    }
    return 1;
  });
  // eslint-disable-next-line no-unused-vars
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [openInfoPanels, setOpenInfoPanels] = useState([]); // Array von offenen Panel-IDs
  const [infoPanelHeights, setInfoPanelHeights] = useState({}); // Dynamische Höhen für InfoPanels
  const [showSchuelerModal, setShowSchuelerModal] = useState(false);
  const [schuelerModalCallback, setSchuelerModalCallback] = useState(null);
  const [currentBarIdForModal, setCurrentBarIdForModal] = useState(null); // Aktuell bearbeitete Bar
  const [barsVisible, setBarsVisible] = useState(true); // Kontrolliert Sichtbarkeit der MovableBars

  const { showEditButtons, user } = useEditing();
  const { planMetadata } = usePlanMetadata();
  const { currentPlanName } = usePlanName();
  const { alert } = useConfirmAlert();

  // Repositioniere Bars basierend auf offenen InfoPanels
  const repositionBarsAfterInfoPanelChange = React.useCallback((activeInfoPanelIds, currentPanelHeights = null) => {
    setBars(prevBars => {
      if (!prevBars || prevBars.length === 0) {
        return prevBars;
      }
      
      // Verwende die übergebenen aktuellen Höhen oder falle zurück auf den State
      const panelHeights = currentPanelHeights || infoPanelHeights;
      
      const sortedBars = [...prevBars].sort((a, b) => a.y - b.y);
      let currentY = 0;
      
      const newBars = sortedBars.map(bar => {
        const barY = currentY;
        currentY += 35; // Standard Bar-Abstand
        
        // Wenn diese Bar ein InfoPanel hat, füge die dynamische Höhe hinzu
        if (activeInfoPanelIds.includes(bar.id)) {
          const panelHeight = panelHeights[bar.id] || 130; // Fallback-Höhe
          currentY += panelHeight;
        }
        
        return { ...bar, y: barY };
      });
      
      // Bessere Überprüfung ob sich die Positionen tatsächlich geändert haben
      if (newBars.length !== prevBars.length) {
        return newBars;
      }
      
      // Erstelle Maps für effizienten Vergleich
      const prevPositions = new Map(prevBars.map(bar => [bar.id, bar.y]));
      const newPositions = new Map(newBars.map(bar => [bar.id, bar.y]));
      
      const positionsChanged = Array.from(newPositions.entries()).some(([id, y]) => {
        return prevPositions.get(id) !== y;
      });
      
      // Nur aktualisieren wenn sich die Positionen geändert haben
      if (positionsChanged) {
        // Informiere Parent über Änderung asynchron um React Render-Zyklus zu vermeiden
        if (onBarsChange) {
          setTimeout(() => onBarsChange(newBars), 0);
        }
        return newBars;
      }
      
      return prevBars;
    });
  }, [infoPanelHeights, onBarsChange]);

  // InfoPanels schließen, wenn Edit-Modus aktiviert wird
  React.useEffect(() => {
    if (showEditButtons) {
      setOpenInfoPanels([]);
      // Setze alle Panel-Höhen auf 0 zurück
      setInfoPanelHeights({});
      // Repositionierung erfolgt automatisch durch openInfoPanels useEffect
    }
  }, [showEditButtons]);
  
  // InfoPanels schließen, wenn Zoom geändert wird (Performance-Optimierung)
  React.useEffect(() => {
    if (isZoomChanging) {
      setOpenInfoPanels([]);
      // Setze alle Panel-Höhen auf 0 zurück
      setInfoPanelHeights({});
      // Repositionierung erfolgt automatisch durch openInfoPanels useEffect
    }
  }, [isZoomChanging]);
  
  // Status der geöffneten InfoPanels nach oben propagieren
  React.useEffect(() => {
    if (onInfoPanelsChange) {
      onInfoPanelsChange(openInfoPanels.length > 0);
    }
    
    // Sende Event sofort, aber nur wenn sich der Status wirklich geändert hat
    const globalEvent = new CustomEvent('infoPanelStatusChange', {
      detail: { 
        taskId: taskIdRef.current,
        hasOpenPanels: openInfoPanels.length > 0,
        openPanelCount: openInfoPanels.length 
      }
    });
    window.dispatchEvent(globalEvent);
    
    // Repositioniere Bars basierend auf den aktuellen offenen Panels
    // OHNE weitere Verzögerung um Sprung-Effekte zu vermeiden
    repositionBarsAfterInfoPanelChange(openInfoPanels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInfoPanels, onInfoPanelsChange]); // repositionBarsAfterInfoPanelChange absichtlich ausgelassen um Endlosschleife zu vermeiden
  
  // Event-Listener für globales "Alle InfoPanels schließen" und Task-Updates
  React.useEffect(() => {
    const handleCloseAllInfoPanels = () => {
      setOpenInfoPanels([]);
      // Setze alle Panel-Höhen auf 0 zurück
      setInfoPanelHeights({});
      // Repositionierung erfolgt jetzt automatisch über den openInfoPanels useEffect
      // Keine zusätzliche setTimeout-Verzögerung mehr nötig
    };

    const handleForceTasksUpdate = () => {
      // Stelle sicher, dass Bars sichtbar sind
      setBarsVisible(true);
      
      // Force repositioning für alle Bars (auch wenn bars Array aktuell leer ist)
      setTimeout(() => {
        repositionBarsAfterInfoPanelChange([]);
        
        // Zusätzlicher Trigger für React Re-render
        window.dispatchEvent(new Event('resize'));
      }, 50);
    };

    window.addEventListener('closeAllInfoPanels', handleCloseAllInfoPanels);
    window.addEventListener('forceTasksUpdate', handleForceTasksUpdate);
    
    // Cleanup: Task aus der globalen Status-Map entfernen
    return () => {
      window.removeEventListener('closeAllInfoPanels', handleCloseAllInfoPanels);
      window.removeEventListener('forceTasksUpdate', handleForceTasksUpdate);
      
      // Task aus der globalen Map entfernen
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const currentTaskId = taskIdRef.current; // Copy ref value to avoid ESLint warning
      const globalEvent = new CustomEvent('infoPanelStatusChange', {
        detail: { 
          taskId: currentTaskId,
          hasOpenPanels: false,
          isCleanup: true
        }
      });
      window.dispatchEvent(globalEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // repositionBarsAfterInfoPanelChange absichtlich ausgelassen um Endlosschleife zu vermeiden
  const { startDate } = useStartDate();
  const { openEditDialog } = useEditBarDialog();
  const { calendarStripsRef } = useCalendarStrips();
  const { pasteTask, hasCopiedTask } = useCopyPaste();
  const { markAsChanged } = useHasChanges();
  
  // Ref to track if updates are internal (from user interaction) or external (from parent)
  const isInternalUpdateRef = useRef(false);
  const isInitialLoadRef = useRef(true); // Track if this is initial load
  
  // Ref für das Haupt-Div-Element
  const taskDivRef = useRef(null);

  // Funktion zur Berechnung der Div-Breite in Pixeln
  const calculateDivWidthInPixels = () => {
    if (taskDivRef.current) {
      const rect = taskDivRef.current.getBoundingClientRect();
      return rect.width;
    }
    // Fallback-Wert falls das Element noch nicht gemountet ist
    return 1662; // Standard-Container-Breite
  };

  // Funktion zur Berechnung der absoluten x-Anfangs- und End-Position des Divs
  const getDivAbsoluteXPositions = () => {
    if (taskDivRef.current) {
      const rect = taskDivRef.current.getBoundingClientRect();
      return {
        startX: rect.left,
        endX: rect.right,
        width: rect.width
      };
    }
    // Fallback-Werte falls das Element noch nicht gemountet ist
    return {
      startX: 0,
      endX: 1662,
      width: 1662
    };
  };

  // Neue Funktion zum Zugriff auf die CalendarStrips-Koordinaten
  const getCalendarXCoordinates = () => {
    if (calendarStripsRef.current && calendarStripsRef.current.getAbsoluteCalendarXCoordinates) {
      return calendarStripsRef.current.getAbsoluteCalendarXCoordinates();
    }
    // Fallback falls CalendarStrips noch nicht verfügbar ist
    return {
      containerStart: 0,
      containerEnd: 0,
      containerWidth: 0,
      weekColumns: []
    };
  };

  // Expose bars data to parent component
  useImperativeHandle(ref, () => ({
    getBars: () => bars,
    setBars: (newBars) => {
      const convertedBars = newBars.map(bar => ({
        ...bar,
        // UUID NICHT automatisch generieren - bestehende UUID beibehalten
        uuid: bar.uuid, // Bestehende UUID beibehalten, keine neue generieren
        startDate: bar.startDate ? new Date(bar.startDate) : undefined,
        endDate: bar.endDate ? new Date(bar.endDate) : undefined
      }));
      setBars(convertedBars);
      if (onBarsChange) {
        setTimeout(() => onBarsChange(convertedBars), 0);
      }
    },
    getDivWidthInPixels: () => calculateDivWidthInPixels(),
    getDivAbsoluteXPositions: () => getDivAbsoluteXPositions(),
    getCalendarXCoordinates: () => getCalendarXCoordinates(),
    closeAllInfoPanels: () => {
      setOpenInfoPanels([]);
      // Setze alle Panel-Höhen auf 0 zurück
      setInfoPanelHeights({});
      // Repositionierung erfolgt automatisch durch openInfoPanels useEffect
    },
    forceRepositioning: () => {
      // Force repositioning of all bars based on their current dates
      if (bars.length > 0) {
        setBars(prevBars => prevBars.map(bar => {
          if (bar.startDate && bar.endDate) {
            const { x, width } = calculateBarPosition(bar.startDate, bar.endDate);
            return { ...bar, x, width };
          }
          return bar;
        }));
        // Also close info panels to prevent layout issues
        setOpenInfoPanels([]);
      }
    },
    ensureBarsVisible: () => {
      // Ensure bars are visible and force a re-render
      setBarsVisible(true);
      // Force repositioning if bars exist
      if (bars.length > 0) {
        setTimeout(() => {
          repositionBarsAfterInfoPanelChange([]);
        }, 10);
      }
    }
  }));

  // Notify parent when bars change (only for internal updates)
  // Deaktiviert um Endlosschleifen zu vermeiden - Parent wird direkt in den Funktionen informiert
  /*
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false; // Skip first render
      return;
    }
    
    if (isInternalUpdateRef.current && onBarsChange) {
      onBarsChange(bars);
    }
    // Reset flag nach dem Update, aber mit einer kleinen Verzögerung
    // um zu verhindern, dass der initialBars useEffect zu früh triggert
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 50);
  }, [bars, onBarsChange]);
  */

  // Funktion zur Berechnung der Kalenderwoche
  // eslint-disable-next-line no-unused-vars
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Berechne Position und Breite basierend auf Start- und Enddatum
  const calculateBarPosition = React.useCallback((barStartDate, barEndDate) => {
    if (!barStartDate || !barEndDate) return { x: 0, width: 10 };
    
    // Die Container-Breite entspricht der aktuellen Ansicht
    const containerWidthPixels = visibleWeeks * weekWidth;
    
    // Verwende das startDate aus dem StartDate-Context
    const viewStartDate = new Date(startDate);
    const dayOfWeek = viewStartDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
    viewStartDate.setHours(0, 0, 0, 0); // Immer auf 00:00:00 setzen
    
    // Berechne Start- und Endposition in Pixeln relativ zum Wochen-Raster
    // Jede Woche ist weekWidth Pixel breit, unabhängig von visibleWeeks
    const msPerWeek = 7 * 24 * 60 * 60 * 1000; // Millisekunden pro Woche
    const pixelsPerMs = weekWidth / msPerWeek;
    
    const startOffsetMs = barStartDate.getTime() - viewStartDate.getTime();
    const endOffsetMs = barEndDate.getTime() - viewStartDate.getTime();
    
    const startPx = startOffsetMs * pixelsPerMs;
    
    const endPx = endOffsetMs * pixelsPerMs;
    
    // Prüfe ob der Task vollständig außerhalb des sichtbaren Bereichs liegt
    if (endPx <= 0 || startPx >= containerWidthPixels) {
      return { x: -100, width: 0 }; // Verstecken
    }
    
    // Begrenze auf sichtbaren Bereich und konvertiere zu Prozent
    const visibleStartPx = Math.max(0, startPx);
    const visibleEndPx = Math.min(containerWidthPixels, endPx);
    const visibleWidthPx = visibleEndPx - visibleStartPx;
    
    // Konvertiere Pixel-Positionen zu Prozentangaben (MovableBar erwartet Prozente)
    // WICHTIG: Auf 4 Dezimalstellen runden, um Rundungsfehler bei grossen Containern zu vermeiden
    // Bei 50 Wochen x 400px = 20.000px Container kann ein Rundungsfehler von 0.001% bereits 20px ausmachen!
    const xPercent = parseFloat(((visibleStartPx / containerWidthPixels) * 100).toFixed(4));
    const widthPercent = parseFloat(((visibleWidthPx / containerWidthPixels) * 100).toFixed(4));
    
    return { x: xPercent, width: Math.max(widthPercent, 0.5) }; // Mindestbreite 0.5%
  }, [visibleWeeks, weekWidth, startDate]);

  // Berechne die benötigte Höhe basierend auf der Anzahl der Bars und offenen InfoPanels
  const calculateHeight = () => {
    if (bars.length === 0) return 100; // Mindesthöhe wenn keine Bars vorhanden
    
    // Finde die maximale Y-Position und addiere die Höhe eines Bars (28px) plus Puffer
    const maxY = Math.max(...bars.map(bar => bar.y));
    const barHeight = 28; // Tatsächliche Höhe eines MovableBar
    
    // Berechne die Gesamthöhe aller offenen InfoPanels
    const totalInfoPanelHeight = openInfoPanels.reduce((total, barId) => {
      return total + (infoPanelHeights[barId] || 130); // Fallback-Höhe
    }, 0);
    
    const bottomPadding = showEditButtons ? 40 : 20; // Extra Platz für den + Button
    
    const calculatedHeight = Math.max(100, maxY + barHeight + totalInfoPanelHeight + bottomPadding);
    /*
      maxY,
      barHeight,
      totalInfoPanelHeight,
      bottomPadding,
      calculatedHeight,
      infoPanelHeights,
      openInfoPanels
    });
    */
    return calculatedHeight;
  };

  const addBar = () => {
    // Erstelle ein Standardzeitraum: 14 Tage, Startdatum aus Einstellungen
    const defaultStartDate = startDate ? new Date(startDate) : new Date();
    const defaultEndDate = new Date(defaultStartDate);
    defaultEndDate.setDate(defaultEndDate.getDate() + 14); // 14 Tage später

    const { x, width } = calculateBarPosition(defaultStartDate, defaultEndDate);

    const newUuid = generateUUID();
    
    const newBar = {
      id: nextBarId,
      uuid: newUuid, // Eindeutige UUID für neuen Task
      x: x,
      y: 0 + (bars.length * 35),
      width: width,
      label: `Nachweis ${nextBarId}`,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      assignedStudents: [] // Initialisiere mit leerer Studentenliste
    };

    isInternalUpdateRef.current = true; // Mark as internal update
    const newBars = [...bars, newBar];
    setBars(newBars);
    setNextBarId(nextBarId + 1);
    
    // Informiere Parent über Änderung
    if (onBarsChange) {
      setTimeout(() => onBarsChange(newBars), 0);
    }
    
    // Markiere als geändert
    markAsChanged();
    
    // Erstelle leere Zuordnung in der Datenbank für neuen Task (falls DB verfügbar)
    // Verwende die newBars direkt und längeren setTimeout für bessere Synchronisation
    if (user && planMetadata.planName) {
      setTimeout(() => {
        // Für neue Tasks ist normalerweise keine sofortige DB-Zuordnung nötig
        // Die Zuordnung wird erstellt, wenn erstmals Lernende hinzugefügt werden
      }, 200);
    }
  };

  const pasteBar = async () => {
    const copiedTaskData = pasteTask();
    if (!copiedTaskData) return;

    // Verwende die gleichen Standarddaten wie beim Hinzufügen
    const today = new Date();
    const defaultStartDate = new Date(today);
    const defaultEndDate = new Date(today);
    defaultEndDate.setDate(defaultEndDate.getDate() + 14);

    // Berechne Position basierend auf Daten oder verwende Standardposition
    let x, width;
    if (copiedTaskData.startDate && copiedTaskData.endDate) {
      const position = calculateBarPosition(copiedTaskData.startDate, copiedTaskData.endDate);
      x = position.x;
      width = position.width;
    } else {
      const position = calculateBarPosition(defaultStartDate, defaultEndDate);
      x = position.x;
      width = position.width;
    }

    const newUuid = generateUUID(); // Neue UUID für kopierte Task
    const newBar = {
      ...copiedTaskData,
      id: nextBarId,
      uuid: newUuid, // Neue UUID für kopierte Task (nicht die alte UUID übernehmen)
      x: x,
      y: bars.length * 35, // Am Ende hinzufügen
      width: width,
      startDate: copiedTaskData.startDate || defaultStartDate,
      endDate: copiedTaskData.endDate || defaultEndDate,
      assignedStudents: [] // Wird aus der DB kopiert, erstmal leer
    };
    
    isInternalUpdateRef.current = true;
    const newBars = [...bars, newBar];
    setBars(newBars);
    setNextBarId(nextBarId + 1);
    
    // Informiere Parent über Änderung
    if (onBarsChange) {
      setTimeout(() => onBarsChange(newBars), 0);
    }
    
    // Markiere als geändert
    markAsChanged();
    
    // Kopiere die Zuordnungen der Lernenden von der ursprünglichen Task-UUID zur neuen UUID
    if (user && planMetadata.planName && copiedTaskData.originalUuid && newUuid) {
      try {
        const copyResult = await copyTaskAssignmentsToNewUUID(
          user?.username || user?.login || user?.id || 'default-user',
          planMetadata.planName,
          planMetadata.planVersion || 1,
          copiedTaskData.originalUuid, // Original UUID
          newUuid // Neue UUID
        );
        
        if (copyResult.success) {
          console.log(`Zuordnungen erfolgreich kopiert: ${copyResult.copiedCount} Lernende`);
          
          // Lade die kopierten Zuordnungen und aktualisiere den lokalen State
          const assignmentsResult = await fetchTaskAssignments(
            user?.username || user?.login || user?.id,
            newUuid,
            planMetadata.planVersion || 1
          );
          
          if (assignmentsResult && !assignmentsResult.error) {
            setBars(prevBars => {
              return prevBars.map(bar => {
                if (bar.id === newBar.id) {
                  return { ...bar, assignedStudents: assignmentsResult.assignments || [] };
                }
                return bar;
              });
            });
          }
        } else {
          console.warn('Fehler beim Kopieren der Zuordnungen:', copyResult.message);
          // Fallback: Leere Zuordnung erstellen
          handleStudentsUpdate(newBar.id, []);
        }
      } catch (error) {
        console.error('Fehler beim Kopieren der Task-Zuordnungen:', error);
        // Fallback: Fehler beim Kopieren der Zuordnungen
      }
    } else if (user && planMetadata.planName) {
      // Kein UUID vorhanden für Zuordnungen
    }
  };

  const updateBar = (id, updates) => {
    isInternalUpdateRef.current = true; // Mark as internal update
    setBars(prevBars => {
      let updatedBars = prevBars.map(bar => {
        if (bar.id === id) {
          const updatedBar = { ...bar, ...updates };
          
          // Wenn Position oder Breite geändert wurde, aktualisiere auch die Daten
          if (updates.x !== undefined || updates.width !== undefined) {
            const dateRange = calculateDateFromPosition(updatedBar.x, updatedBar.width);
            
            // Wenn nur Position geändert wurde (Drag), Dauer beibehalten
            if (updates.x !== undefined && updates.width === undefined) {
              const durationMs = bar.endDate.getTime() - bar.startDate.getTime();
              
              updatedBar.startDate = dateRange.startDate;
              updatedBar.startDate.setHours(0, 0, 0, 0);
              updatedBar.endDate = new Date(updatedBar.startDate.getTime() + durationMs);
            } else if (updates.width !== undefined && updates.x === undefined) {
              // Nur Resize: linken Rand/Start beibehalten, nur Ende anpassen
              updatedBar.startDate = new Date(bar.startDate);
              updatedBar.startDate.setHours(0, 0, 0, 0);
              updatedBar.endDate = dateRange.endDate;
            } else {
              // Resize oder beides: Beide auf Tagesgrenzen runden
              updatedBar.startDate = dateRange.startDate;
              updatedBar.endDate = dateRange.endDate;
            }
            
            // Position zurückberechnen, damit der Balken visuell auf den Tag "snapped"
            const snappedPosition = calculateBarPosition(updatedBar.startDate, updatedBar.endDate);
            updatedBar.x = snappedPosition.x;
            updatedBar.width = snappedPosition.width;
          }
          
          // Wenn Daten geändert wurden, aktualisiere auch Position und Breite
          if (updates.startDate !== undefined || updates.endDate !== undefined) {
            const position = calculateBarPosition(updatedBar.startDate, updatedBar.endDate);
            updatedBar.x = position.x;
            updatedBar.width = position.width;
          }
          
          return updatedBar;
        }
        return bar;
      });
      
      // Wenn Y-Position geändert wurde, prüfe auf Kollisionen und verschiebe andere Bars
      if (updates.y !== undefined) {
        const gridSize = 35;
        const targetY = updates.y;
        const movedBar = updatedBars.find(bar => bar.id === id);
        
        if (movedBar) {
          // Finde alle anderen Bars auf der Zielposition
          const conflictingBars = updatedBars.filter(bar => 
            bar.id !== id && bar.y === targetY
          );
          
          if (conflictingBars.length > 0) {
            // Verschiebe konfliktende Bars
            conflictingBars.forEach(conflictBar => {
              // Finde eine freie Position für den konfliktenden Bar
              let newY = conflictBar.y;
              let attempts = 0;
              const maxAttempts = 20; // Verhindere endlose Schleifen
              
              while (attempts < maxAttempts) {
                // Versuche erst nach unten zu verschieben
                const candidateY = newY + gridSize;
                const isOccupied = updatedBars.some(bar => 
                  bar.id !== conflictBar.id && bar.id !== id && bar.y === candidateY
                );
                
                if (!isOccupied) {
                  // Position ist frei
                  updatedBars = updatedBars.map(bar => 
                    bar.id === conflictBar.id ? { ...bar, y: candidateY } : bar
                  );
                  break;
                } else {
                  newY = candidateY;
                  attempts++;
                }
              }
            });
          }
        }
        
        // Kompaktiere alle Bars nach der Verschiebung
        updatedBars = compactBars(updatedBars);
      }
      
      // Informiere Parent über Änderungen asynchron um React Render-Zyklus zu vermeiden
      if (onBarsChange) {
        setTimeout(() => onBarsChange(updatedBars), 0);
      }
      
      // Markiere als geändert asynchron um React Render-Zyklus zu vermeiden
      setTimeout(() => markAsChanged(), 0);
      
      return updatedBars;
    });
  };

  // Hilfsfunktion zum Sortieren der Bars basierend auf X-Position (chronologisch)
  const sortBarsByPosition = (bars) => {
    return bars.sort((a, b) => a.x - b.x).map((bar, index) => ({
      ...bar,
      y: index * 35 // Standardisierte Y-Positionen mit 35px Abstand
    }));
  };

  // Berechne Daten aus Position (umgekehrte Funktion)
  const calculateDateFromPosition = (x, width) => {
    // Die Container-Breite entspricht der aktuellen Ansicht
    const containerWidthPixels = visibleWeeks * weekWidth;
    
    // Verwende das startDate aus dem StartDate-Context
    const viewStartDate = new Date(startDate);
    const dayOfWeek = viewStartDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
    viewStartDate.setHours(0, 0, 0, 0); // Immer auf 00:00:00 setzen
    
    // Konvertiere Prozentangaben zu Pixeln
    const startPx = (x / 100) * containerWidthPixels;
    const widthPx = (width / 100) * containerWidthPixels;
    
    // Berechne Millisekunden pro Pixel basierend auf fester Wochenbreite
    const msPerWeek = 7 * 24 * 60 * 60 * 1000; // Millisekunden pro Woche
    const msPerPixel = msPerWeek / weekWidth;
    
    // Konvertiere Pixel-Positionen zurück zu Daten
    const startDateCalculated = new Date(viewStartDate.getTime() + (startPx * msPerPixel));
    const endDateCalculated = new Date(viewStartDate.getTime() + ((startPx + widthPx) * msPerPixel));
    
    // Auf Tagesgrenzen runden: Start = 00:00:00, Ende = 23:59:59
    startDateCalculated.setHours(0, 0, 0, 0);
    endDateCalculated.setHours(23, 59, 59, 999);
    
    return { startDate: startDateCalculated, endDate: endDateCalculated };
  };

  // Ref um zu verfolgen, ob visibleWeeks gerade geändert wird
  const prevIsVisibleWeeksChangingRef = useRef(isVisibleWeeksChanging);
  const prevVisibleWeeksRef = useRef(visibleWeeks); // Ref um die vorherige visibleWeeks zu speichern
  const debounceTimerRef = useRef(null); // Timer für Debouncing
  const lastVisibleWeeksValueRef = useRef(visibleWeeks); // Letzter stabiler Wert
  
  // Effect für isVisibleWeeksChanging - kontrolliert Sichtbarkeit und Repositionierung
  useEffect(() => {
    if (isVisibleWeeksChanging) {
      // Verstecke MovableBars sofort beim Start der Änderung
      setBarsVisible(false);
      // Schließe alle InfoPanels für bessere Performance
      setOpenInfoPanels([]);
      // Repositioniere Bars nach dem Schließen der Panels
      setTimeout(() => {
        repositionBarsAfterInfoPanelChange([]);
      }, 0);
      
      // Lösche vorherige Debounce Timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    } else {
      // Wenn Slider losgelassen wird, starte Debounce Timer
      if (prevIsVisibleWeeksChangingRef.current === true) {
        // Lösche vorherige Timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        
        // Debounce: Warte kurz, falls weitere Änderungen kommen
        const targetVisibleWeeks = visibleWeeks; // Capture current value
        debounceTimerRef.current = setTimeout(() => {
          // Prüfe ob sich der Wert wirklich geändert hat UND ob wir immer noch beim gleichen Zielwert sind
          if (lastVisibleWeeksValueRef.current !== targetVisibleWeeks && 
              targetVisibleWeeks === visibleWeeks && // Zusätzliche Prüfung: Wert ist stabil
              bars.length > 0 && 
              !repositioningRef.current && 
              !isVisibleWeeksChanging) { // Zusätzliche Prüfung: Nicht gerade in Änderung
            
            repositioningRef.current = true;
            
            // Verwende mehrere requestAnimationFrame für stabileres Timing
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Nochmalige Prüfung vor der eigentlichen Repositionierung
                if (targetVisibleWeeks === visibleWeeks && !isVisibleWeeksChanging) {
                  // Bei visibleWeeks-Änderung: Position basierend auf tatsächlichen Daten neu berechnen
                  const updatedBars = bars.map(bar => {
                    if (bar.startDate && bar.endDate) {
                      const { x, width } = calculateBarPosition(bar.startDate, bar.endDate);
                      return { ...bar, x, width };
                    }
                    return bar;
                  });
                  
                  setBars(updatedBars);
                  
                  // Update der Referenz-Werte
                  prevVisibleWeeksRef.current = targetVisibleWeeks;
                  lastVisibleWeeksValueRef.current = targetVisibleWeeks;
                  
                  // Warte bis alle State-Updates und DOM-Renders abgeschlossen sind
                  requestAnimationFrame(() => {
                    repositioningRef.current = false;
                    setBarsVisible(true);
                    debounceTimerRef.current = null;
                  });
                } else {
                  // Wert hat sich während Debounce wieder geändert - abbrechen
                  repositioningRef.current = false;
                  debounceTimerRef.current = null;
                }
              });
            });
          } else {
            // Keine Repositionierung nötig - zeige Bars sofort
            setBarsVisible(true);
            debounceTimerRef.current = null;
          }
        }, 50); // 50ms Debounce-Verzögerung für schnelle Klicks (reduziert für bessere Responsiveness)
      }
    }
    
    // Update der vorherigen isVisibleWeeksChanging
    prevIsVisibleWeeksChangingRef.current = isVisibleWeeksChanging;
    
    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisibleWeeksChanging, visibleWeeks]); // Abhängigkeiten für bessere Kontrolle

  // Effect um Balken neu zu positionieren wenn sich weekWidth ändert (Zoom)
  useEffect(() => {    
    if (bars.length > 0 && !repositioningRef.current) {
      repositioningRef.current = true;
      
      // Berechne neue visuelle Positionen basierend auf bestehenden effektiven Daten
      const updatedBars = bars.map(bar => {
        if (bar.startDate && bar.endDate) {
          const { x, width } = calculateBarPosition(bar.startDate, bar.endDate);
          return { ...bar, x, width };
        }
        return bar;
      });
      
      setBars(updatedBars);
      
      // Reset flag after state update
      setTimeout(() => {
        repositioningRef.current = false;
      }, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekWidth]); // Nur bei weekWidth-Änderungen (Zoom)
  
  // Effect um Balken neu zu positionieren wenn sich startDate ändert
  useEffect(() => {    
    if (bars.length > 0 && !repositioningRef.current) {
      repositioningRef.current = true;
      
      // Berechne neue visuelle Positionen basierend auf bestehenden effektiven Daten
      const updatedBars = bars.map(bar => {
        if (bar.startDate && bar.endDate) {
          const { x, width } = calculateBarPosition(bar.startDate, bar.endDate);
          return { ...bar, x, width };
        }
        return bar;
      });
      
      setBars(updatedBars);
      
      // Reset flag after state update
      setTimeout(() => {
        repositioningRef.current = false;
      }, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]); // Nur bei startDate-Änderungen

  // Zusätzlicher Effect um Bars neu zu positionieren wenn sich die DOM-Geometrie ändert
  useEffect(() => {
    const repositionBars = () => {
      if (bars.length > 0 && !repositioningRef.current) {
        repositioningRef.current = true;
        
        setBars(prevBars => 
          prevBars.map(bar => {
            if (bar.startDate && bar.endDate) {
              const { x, width } = calculateBarPosition(bar.startDate, bar.endDate);
              return { ...bar, x, width };
            }
            return bar;
          })
        );
        
        setTimeout(() => {
          repositioningRef.current = false;
        }, 100);
      }
    };

    // ResizeObserver um auf Größenänderungen des Containers zu reagieren
    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line no-unused-vars
      for (const entry of entries) {
        repositionBars();
      }
    });

    if (taskDivRef.current) {
      resizeObserver.observe(taskDivRef.current);
    }

    // Listen to window resize events as well
    const handleWindowResize = () => {
      repositionBars();
    };
    
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [bars.length, calculateBarPosition]); // Added missing dependencies

  const deleteBar = async (id) => {
    const barToDelete = bars.find(bar => bar.id === id);
    
    // Lösche Zuordnungen in der Datenbank vor dem Löschen der Bar (falls DB verfügbar)
    if (barToDelete && user && planMetadata.planName) {
      try {
        await handleStudentsUpdate(id, []); // Leere Zuordnung = Löschen aller Zuordnungen
      } catch (error) {
        console.error('Fehler beim Löschen der Task-Zuordnungen:', error);
        // Fortfahren mit dem Löschen der Bar auch bei DB-Fehler
      }
    }
    
    const remainingBars = bars.filter(bar => bar.id !== id);
    
    // Repositioniere die verbleibenden Bars automatisch
    const repositionedBars = remainingBars.map((bar, index) => ({
      ...bar,
      y: index * 35 // Standardisierte Y-Positionen
    }));
    
    // Sofortige lokale Aktualisierung
    isInternalUpdateRef.current = true; // Mark as internal update
    setBars(repositionedBars);
    
    // Informiere Parent über Änderung mit mehrfachen Versuchen für Robustheit
    if (onBarsChange) {
      // Sofortige synchrone Aktualisierung
      onBarsChange(repositionedBars);
      
      // Mehrfache asynchrone Aktualisierungen mit verschiedenen Delays
      setTimeout(() => onBarsChange(repositionedBars), 10);
      setTimeout(() => onBarsChange(repositionedBars), 50);
      setTimeout(() => onBarsChange(repositionedBars), 200);
    }
    
    // Markiere als geändert
    markAsChanged();
    
    console.log(`Task ${id} gelöscht. Verbleibende Tasks:`, repositionedBars.length);
  };

  // Funktionen für vertikale Verschiebung
  const handleDragStart = (barId, currentY) => {
    // Nicht mehr benötigt, aber für Kompatibilität beibehalten
  };

  const handleDragMove = (barId, currentY) => {
    // Nicht mehr benötigt, aber für Kompatibilität beibehalten
  };

  const handleVerticalMove = (barId, newY) => {
    // Stelle sicher, dass alle Bars in eindeutigen Raster-Positionen sind
    setTimeout(() => {
      isInternalUpdateRef.current = true;
      setBars(prevBars => {
        // Finde die optimale Raster-Position für den bewegten Bar
        const snappedY = snapToGrid(prevBars, barId, newY);
        
        // Aktualisiere den bewegten Bar
        const updatedBars = prevBars.map(bar => 
          bar.id === barId ? { ...bar, y: snappedY } : bar
        );
        
        // Kompaktiere alle Bars (entferne Lücken)
        const finalBars = compactBars(updatedBars);
        
        // Markiere als geändert asynchron um React Render-Zyklus zu vermeiden
        setTimeout(() => markAsChanged(), 0);
        
        return finalBars;
      });
    }, 50);
  };

  // Handler für den Up-Button: Tauscht Positionen mit dem darüberliegenden Balken
  const handleMoveUp = (barId) => {
    setBars(prevBars => {
      const currentBar = prevBars.find(bar => bar.id === barId);
      if (!currentBar) return prevBars;

      const sortedBars = [...prevBars].sort((a, b) => a.y - b.y);
      const currentIndex = sortedBars.findIndex(bar => bar.id === barId);

      if (currentIndex <= 0) {
        return prevBars;
      }

      const aboveBar = sortedBars[currentIndex - 1];
      const currentBarY = currentBar.y;
      const aboveBarY = aboveBar.y;

      const updatedBars = prevBars.map(bar => {
        if (bar.id === currentBar.id) {
          return { ...bar, y: aboveBarY };
        } else if (bar.id === aboveBar.id) {
          return { ...bar, y: currentBarY };
        }
        return bar;
      });

      if (onBarsChange) {
        setTimeout(() => onBarsChange(updatedBars), 0);
      }

      setTimeout(() => markAsChanged(), 0);

      return updatedBars;
    });
  };

  // Handler für den Down-Button: Tauscht Positionen mit dem darunterliegenden Balken
  const handleMoveDown = (barId) => {
    setBars(prevBars => {
      // Finde den aktuellen Bar
      const currentBar = prevBars.find(bar => bar.id === barId);
      if (!currentBar) return prevBars;
      
      // Sortiere alle Bars nach Y-Position
      const sortedBars = [...prevBars].sort((a, b) => a.y - b.y);
      const currentIndex = sortedBars.findIndex(bar => bar.id === barId);
      
      // Prüfe ob es einen darunterliegenden Bar gibt
      if (currentIndex >= sortedBars.length - 1) {
        return prevBars; // Kein Bar darunter vorhanden
      }
      
      // Finde den darunterliegenden Bar
      const belowBar = sortedBars[currentIndex + 1];
      
      // Tausche die Y-Positionen der beiden Bars
      const currentBarY = currentBar.y;
      const belowBarY = belowBar.y;
      
      // Aktualisiere beide Bars mit getauschten Positionen
      const updatedBars = prevBars.map(bar => {
        if (bar.id === currentBar.id) {
          return { ...bar, y: belowBarY };
        } else if (bar.id === belowBar.id) {
          return { ...bar, y: currentBarY };
        }
        return bar;
      });
      
      // Informiere Parent-Komponente
      if (onBarsChange) {
        setTimeout(() => onBarsChange(updatedBars), 0);
      }
      
      // Markiere als geändert asynchron um React Render-Zyklus zu vermeiden
      setTimeout(() => markAsChanged(), 0);
      
      return updatedBars;
    });
  };

  // Hilfsfunktion zum Einrasten in das Raster und Vermeiden von Überlappungen
  const snapToGrid = (bars, draggedBarId, newY) => {
    const gridSize = 35;
    const targetGridPos = Math.round(newY / gridSize) * gridSize;
    
    // Finde alle anderen Bars (nicht der aktuell gezogene)
    const otherBars = bars.filter(bar => bar.id !== draggedBarId);
    
    // Prüfe ob die Zielposition bereits belegt ist
    const isPositionOccupied = otherBars.some(bar => bar.y === targetGridPos);
    
    if (!isPositionOccupied) {
      // Position ist frei, kann verwendet werden
      return targetGridPos;
    }
    
    // Finde die nächste freie Position
    let freePosition = targetGridPos;
    let direction = 1; // 1 = nach unten, -1 = nach oben
    let offset = gridSize;
    
    while (true) {
      const candidatePos = targetGridPos + (direction * offset);
      
      // Prüfe ob Position negativ ist (nach oben)
      if (candidatePos < 0) {
        direction = 1; // Wechsel zu nach unten
        offset += gridSize;
        continue;
      }
      
      // Prüfe ob Position frei ist
      const isOccupied = otherBars.some(bar => bar.y === candidatePos);
      if (!isOccupied) {
        freePosition = candidatePos;
        break;
      }
      
      // Wechsel die Richtung oder erhöhe den Offset
      if (direction === 1) {
        direction = -1; // Wechsel zu nach oben
      } else {
        direction = 1; // Wechsel zu nach unten
        offset += gridSize;
      }
    }
    
    return freePosition;
  };

  const handleDragEnd = (barId, newY) => {
    handleVerticalMove(barId, newY);
  };

  // Hilfsfunktion zum Kompaktieren der Bars (entfernt Lücken)
  const compactBars = (bars) => {
    const gridSize = 35;
    
    // Sortiere alle Bars nach ihrer Y-Position
    const sortedBars = [...bars].sort((a, b) => a.y - b.y);
    
    // Weise jedem Bar eine kompakte Position zu (ohne Lücken)
    return sortedBars.map((bar, index) => ({
      ...bar,
      y: index * gridSize
    }));
  };

  // Funktion zum manuellen chronologischen Sortieren der Bars (nur über Button)
  const sortBars = () => {
    isInternalUpdateRef.current = true;
    const sortedBars = sortBarsByPosition([...bars]);
    setBars(sortedBars);
    
    // Informiere Parent über Änderung
    if (onBarsChange) {
      setTimeout(() => onBarsChange(sortedBars), 0);
    }
    
    // Markiere als geändert
    markAsChanged();
  };

  // Edit Dialog Handler
  const handleEditBar = (barId, barData) => {
    const bar = bars.find(b => b.id === barId);
    if (bar) {
      openEditDialog(
        { id: barId, ...bar }, // Verwende die aktuellen Daten aus dem bars Array
        (id, updates) => updateBar(id, updates),
        (id) => deleteBar(id)
      );
    }
  };

  // Info Panel Handler - unterstützt mehrere gleichzeitig offene Panels
  const handleToggleInfoPanel = (barId = null) => {
    if (!barId) return;
    
    const isCurrentlyOpen = openInfoPanels.includes(barId);
    
    if (isCurrentlyOpen) {
      // Schließe das Panel für diese Bar
      const newOpenPanels = openInfoPanels.filter(id => id !== barId);
      setOpenInfoPanels(newOpenPanels);
      
      // Setze Panel-Höhe sofort auf 0 für geschlossenes Panel
      setInfoPanelHeights(prev => ({
        ...prev,
        [barId]: 0
      }));
      
      // Repositionierung erfolgt automatisch durch openInfoPanels useEffect
    } else {
      // Öffne das Panel für diese Bar
      const newOpenPanels = [...openInfoPanels, barId];
      setOpenInfoPanels(newOpenPanels);
      // Repositionierung erfolgt automatisch durch openInfoPanels useEffect + handleInfoPanelHeightChange
    }
    
    // Setze showInfoPanel auf true wenn mindestens ein Panel offen ist
    setShowInfoPanel(openInfoPanels.length > 0 || !isCurrentlyOpen);
  };

  const handleCloseInfoPanel = (barId) => {
    const newOpenPanels = openInfoPanels.filter(id => id !== barId);
    setOpenInfoPanels(newOpenPanels);
    setShowInfoPanel(newOpenPanels.length > 0);
    
    // Setze Panel-Höhe sofort auf 0 für geschlossenes Panel
    setInfoPanelHeights(prev => ({
      ...prev,
      [barId]: 0
    }));
    
    // Repositionierung erfolgt automatisch durch openInfoPanels useEffect
  };

  // SchuelerModal Handler
  const handleOpenSchuelerModal = async (barId, callback) => {
    // Prüfe ob Plan gespeichert werden muss
    if (currentPlanName === 'unbenannt') {
      await alert(
        'Bevor Sie Lernende zuordnen können, muss der Plan gespeichert werden.\n\nBitte speichern Sie Ihren Plan zuerst (Ctrl+S oder "Speichern als").',
        { title: "Plan speichern erforderlich" }
      );
      return;
    }
    
    setSchuelerModalCallback(() => callback);
    setCurrentBarIdForModal(barId);
    setShowSchuelerModal(true);
  };

  const handleCloseSchuelerModal = () => {
    setShowSchuelerModal(false);
    setSchuelerModalCallback(null);
    setCurrentBarIdForModal(null);
  };

  const handleUebernehmenLernende = (selectedLernendeIds, lernendeData, actionMode = 'add', klassensynchronisation = '') => {
    if (schuelerModalCallback) {
      schuelerModalCallback(selectedLernendeIds, lernendeData, actionMode, klassensynchronisation);
    }
    
    // Speichere Klassensynchronisation in der aktuellen Task
    if (currentBarIdForModal && klassensynchronisation !== undefined) {
      setBars(prevBars => {
        const updatedBars = prevBars.map(bar => {
          if (bar.id === currentBarIdForModal) {
            return { ...bar, klassensynchronisation };
          }
          return bar;
        });
        
        // Informiere Parent-Komponente über die Änderung
        isInternalUpdateRef.current = true;
        if (onBarsChange) {
          setTimeout(() => onBarsChange(updatedBars), 0);
        }
        return updatedBars;
      });
    }
    
    handleCloseSchuelerModal();
  };

  // Handler für dynamische Höhenänderungen der InfoPanels
  const repositioningRef = React.useRef(false);
  
  const handleInfoPanelHeightChange = React.useCallback((barId, height) => {
    setInfoPanelHeights(prev => {
      const newHeights = { ...prev, [barId]: height };
      
      // Nur repositionieren wenn sich die Höhe tatsächlich geändert hat
      if (prev[barId] !== height) {
        // Repositioniere sofort mit den neuen Höhen
        // Verwende einen kurzen setTimeout um Race Conditions zu vermeiden
        setTimeout(() => {
          // Verwende eine Ref um die aktuellen openInfoPanels zu bekommen
          setOpenInfoPanels(currentOpenPanels => {
            repositionBarsAfterInfoPanelChange(currentOpenPanels, newHeights);
            return currentOpenPanels; // Keine Änderung, nur Zugriff auf aktuellen Wert
          });
        }, 50);
      }
      
      return newHeights;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // repositionBarsAfterInfoPanelChange absichtlich ausgelassen um Endlosschleife zu vermeiden

  // Handler für das Hinzufügen von Studenten zu einer Bar
  const handleStudentsUpdate = async (barId, students, actionMode = 'replace') => {
    const bar = bars.find(b => b.id === barId);
    if (!bar) {
      console.warn('Task-Bar nicht gefunden für ID:', barId);
      return;
    }

    // Prüfe ob Task eine UUID hat - für Legacy-Tasks ohne UUID keine DB-Speicherung möglich
    if (!bar.uuid) {
      // Nur lokalen State aktualisieren für Legacy-Tasks
      setBars(prevBars => {
        return prevBars.map(b => {
          if (b.id === barId) {
            return { 
              ...b, 
              assignedStudents: actionMode === 'add' 
                ? [...(b.assignedStudents || []), ...(students || [])]
                : (students || [])
            };
          }
          return b;
        });
      });
      
      // Früh beenden für Legacy-Tasks
      return;
    }

    // Wenn keine DB-Integration möglich ist, aktualisiere nur lokal
    if (!user || !planMetadata.planName) {
      console.warn('Keine DB-Integration möglich - speichere nur lokal');
      
      // Lokalen State aktualisieren (Fallback-Verhalten)
      setBars(prevBars => {
        const updatedBars = prevBars.map(b => {
          if (b.id === barId) {
            return { ...b, assignedStudents: students };
          }
          return b;
        });
        
        // Informiere Parent-Komponente über die Änderung
        isInternalUpdateRef.current = true;
        if (onBarsChange) {
          setTimeout(() => onBarsChange(updatedBars), 0);
        }
        return updatedBars;
      });
      return;
    }

    try {
      // Validiere und säubere die Daten vor dem API-Aufruf
      const cleanedStudents = Array.isArray(students) ? students : [];
      
      // Transformiere Studentendaten für Backend-Kompatibilität
      const transformedStudents = cleanedStudents.map(student => ({
        login: student.login,
        vorname: student.vorname,
        nachname: student.nachname,
        schuljahrId: student.schuljahrId, // Sollte jetzt vom SchuelerModal kommen
        klasse: student.klasse,
        klassenId: student.klasseId // Backend erwartet klassenId
      }));
      
      // Validierung der benötigten Parameter
      if (!bar.uuid) {
        return;
      }
      
      const apiParams = {
        userId: user?.username || user?.login || user?.id || 'default-user',
        planName: planMetadata.planName,
        planVersion: planMetadata.planVersion || 1,
        taskUuid: bar.uuid,
        studentsData: transformedStudents
      };
      
      // Speichere Zuordnungen in der Datenbank
      let result;
      if (actionMode === 'add') {
        // Bei 'add' nur neue Studenten hinzufügen (Backend ignoriert Duplikate)
        // Finde heraus, welche Studenten wirklich neu sind
        const currentAssignedStudents = bar.assignedStudents || [];
        const existingLogins = new Set(currentAssignedStudents.map(s => s.login));
        const newStudentsOnly = transformedStudents.filter(s => !existingLogins.has(s.login));
        
        if (newStudentsOnly.length > 0) {
          result = await addTaskAssignments(
            apiParams.userId,
            apiParams.planName,
            apiParams.planVersion,
            apiParams.taskUuid,
            newStudentsOnly
          );
        } else {
          // Keine neuen Studenten zu hinzufügen
          result = { success: true, message: 'Keine neuen Studenten zum Hinzufügen' };
        }
      } else {
        // Bei 'remove' oder 'replace' vollständige Liste verwenden
        result = await updateTaskAssignments(
          apiParams.userId,
          apiParams.planName,
          apiParams.planVersion,
          apiParams.taskUuid,
          apiParams.studentsData
        );
      }

      if (result.error) {
        console.error('Fehler beim Speichern der Task-Zuordnungen:', result.error);
        // Fallback: Speichere nur lokal
        setBars(prevBars => {
          const updatedBars = prevBars.map(b => {
            if (b.id === barId) {
              return { ...b, assignedStudents: students };
            }
            return b;
          });
          
          isInternalUpdateRef.current = true;
          if (onBarsChange) {
            setTimeout(() => onBarsChange(updatedBars), 0);
          }
          return updatedBars;
        });
        return;
      }

      // Lokalen State aktualisieren nur bei erfolgreichem DB-Update
      setBars(prevBars => {
        const updatedBars = prevBars.map(b => {
          if (b.id === barId) {
            return { ...b, assignedStudents: students };
          }
          return b;
        });
        
        // Informiere Parent-Komponente über die Änderung
        isInternalUpdateRef.current = true;
        if (onBarsChange) {
          setTimeout(() => onBarsChange(updatedBars), 0);
        }
        return updatedBars;
      });
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Task-Zuordnungen:', error);
      // Fallback: Speichere nur lokal
      setBars(prevBars => {
        const updatedBars = prevBars.map(b => {
          if (b.id === barId) {
            return { ...b, assignedStudents: students };
          }
          return b;
        });
        
        isInternalUpdateRef.current = true;
        if (onBarsChange) {
          setTimeout(() => onBarsChange(updatedBars), 0);
        }
        return updatedBars;
      });
    }
  };

  // Handler für die Aktualisierung von Task-Daten (z.B. Klassensynchronisation)
  const handleTaskDataUpdate = (barId, updateData) => {
    setBars(prevBars => {
      const updatedBars = prevBars.map(bar => {
        if (bar.id === barId) {
          return { ...bar, ...updateData };
        }
        return bar;
      });
      
      // Informiere Parent-Komponente über die Änderung
      isInternalUpdateRef.current = true;
      if (onBarsChange) {
        setTimeout(() => onBarsChange(updatedBars), 0);
      }
      return updatedBars;
    });
  };

  // Prüfung ob der Benutzer Edit-Berechtigung hat
  const hasEditPermission = user && user.canEdit;

  // Funktion zum Laden der Zuordnungen aus der Datenbank
  const loadTaskAssignments = React.useCallback(async (taskBars) => {
    if (!taskBars || taskBars.length === 0) {
      return taskBars;
    }

    // Wenn keine DB-Integration möglich ist, verwende leere assignedStudents
    // Legacy JSON assignedStudents werden beim Backend-Loading automatisch entfernt
    if (!user || !planMetadata.planName) {
      return taskBars; // Bars haben leere assignedStudents (keine DB-Integration)
    }

    // Für merged Plans (Übersichtspläne) keine Task-Zuordnungen laden
    if (planMetadata.planVersion === 'merged' || planMetadata.version === 'merged') {
      return taskBars.map(bar => ({ ...bar, assignedStudents: [] }));
    }

    try {
      // Lade Zuordnungen für alle Tasks parallel
      const assignmentPromises = taskBars.map(async (bar) => {
        if (!bar.uuid) {
          // Für Legacy-Tasks ohne UUID: assignedStudents Array leeren damit keine veralteten Daten angezeigt werden
          return { ...bar, assignedStudents: [] };
        }

        const result = await fetchTaskAssignments(
          user?.username || user?.login || user?.id,
          bar.uuid,
          planMetadata.planVersion || 1
        );

        if (result.error) {
          console.warn(`Fehler beim Laden der Zuordnungen für Task ${bar.uuid}:`, result.error);
          return bar; // Fallback zu ursprünglichen Daten
        }

        return {
          ...bar,
          assignedStudents: result.assignments || []
        };
      });

      const barsWithAssignments = await Promise.all(assignmentPromises);
      return barsWithAssignments;
    } catch (error) {
      console.error('Fehler beim Laden der Task-Zuordnungen:', error);
      return taskBars; // Fallback zu ursprünglichen Bars
    }
  }, [user, planMetadata.planName, planMetadata.planVersion, planMetadata.version]);

  // Track loaded plan to prevent infinite loops
  const [loadedPlanKey, setLoadedPlanKey] = useState(null);
  const [lastProcessedInitialBars, setLastProcessedInitialBars] = useState(null);

  const createInitialBarsSyncKey = React.useCallback((barsToHash = []) => {
    const toIsoOrNull = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    };

    const normalizedBars = (barsToHash || []).map(bar => ({
      id: bar.id,
      uuid: bar.uuid || null,
      startDate: toIsoOrNull(bar.startDate),
      endDate: toIsoOrNull(bar.endDate)
    }));

    normalizedBars.sort((a, b) => {
      if (a.id === b.id) return 0;
      return a.id > b.id ? 1 : -1;
    });

    return JSON.stringify(normalizedBars);
  }, []);

  // Convert string dates to Date objects when initialBars change
  useEffect(() => {
    // Prüfe ob sich initialBars geändert haben und nicht leer sind
    if (initialBars && initialBars.length > 0) {
      // Create unique keys for comparison
      const currentPlanKey = planMetadata.planName
        ? `${planMetadata.planName}`
        : null;
      
      const initialBarsKey = createInitialBarsSyncKey(initialBars);
      
      // Check if we need to process this data
      const shouldProcess = lastProcessedInitialBars !== initialBarsKey || 
                           (currentPlanKey && currentPlanKey !== loadedPlanKey);
      
      if (shouldProcess) {
        
        // Update tracking state
        setLastProcessedInitialBars(initialBarsKey);
        if (currentPlanKey) {
          setLoadedPlanKey(currentPlanKey);
        }
        
        const convertedBars = initialBars.map(bar => {
          // Prüfe ob dieser Task bereits existiert (anhand der ID) in den aktuellen bars
          const existingBar = bars.find(existingBar => existingBar.id === bar.id);
          
          const convertedBar = {
            ...bar,
            // Wenn der Task bereits existiert, behalte die bestehende UUID
            // Nur bei komplett neuen Tasks ohne existierende UUID eine generieren
            uuid: existingBar ? existingBar.uuid : (bar.uuid || generateUUID()),
            startDate: bar.startDate ? new Date(bar.startDate) : undefined,
            endDate: bar.endDate ? new Date(bar.endDate) : undefined
          };

          if (convertedBar.startDate && convertedBar.endDate) {
            const recalculatedPosition = calculateBarPosition(convertedBar.startDate, convertedBar.endDate);
            convertedBar.x = recalculatedPosition.x;
            convertedBar.width = recalculatedPosition.width;
          }

          return convertedBar;
        });
        
        // Lade Zuordnungen aus der Datenbank und aktualisiere Bars
        loadTaskAssignments(convertedBars).then(barsWithAssignments => {
          setBars(barsWithAssignments);
        });
        
        // Stelle sicher, dass keine InfoPanels offen sind beim Laden
        setOpenInfoPanels([]);
        
        // Initialisiere infoPanelHeights für alle Bars mit 0
        const initialHeights = {};
        initialBars.forEach(bar => {
          initialHeights[bar.id] = 0;
        });
        setInfoPanelHeights(initialHeights);
        
        // Stelle sicher, dass Bars sichtbar sind
        setBarsVisible(true);
        
        // Repositioniere explizit alle Bars ohne offene Panels
        setTimeout(() => {
          repositionBarsAfterInfoPanelChange([]);
        }, 50); // Etwas längere Verzögerung für bessere Synchronisation
        
        // Nach dem ersten Laden deaktivieren
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      }
    } else if (initialBars && initialBars.length === 0 && bars.length > 0) {
      // Wenn initialBars explizit leer ist, lösche auch die lokalen bars
      setBars([]);
      setOpenInfoPanels([]);
      setInfoPanelHeights({});
      setLastProcessedInitialBars(null);
      setLoadedPlanKey(null);
    }
  }, [initialBars, planMetadata.planName, repositionBarsAfterInfoPanelChange, loadTaskAssignments, loadedPlanKey, lastProcessedInitialBars, createInitialBarsSyncKey, calculateBarPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ 
      position: 'relative', 
      minHeight: `${calculateHeight()}px`,
      width: `${visibleWeeks * weekWidth}px`, // Feste Breite basierend auf Zoom
      minWidth: `${visibleWeeks * weekWidth}px`, // Mindestbreite ebenfalls fest
    }} ref={taskDivRef}>
      
      
      {/* Buttons - nur im Edit-Modus sichtbar */}
      {showEditButtons && (
        <div style={{ 
          position: 'absolute', 
          bottom: '5px', 
          left: '0px',
          width: '100%',
          display: 'flex',
          zIndex: 15
        }}>
          <div style={{
            position: 'sticky',
            left: '5px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
          {/* + Button */}
          <button
            onClick={addBar}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#218838';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#28a745';
              e.target.style.transform = 'scale(1)';
            }}
            title="Neuen Nachweis hinzufügen"
          >
            +
          </button>

          {/* Paste Button - nur sichtbar wenn etwas kopiert wurde */}
          {hasCopiedTask() && (
            <button
              onClick={pasteBar}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#138496';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#17a2b8';
                e.target.style.transform = 'scale(1)';
              }}
              title="Kopierten Nachweis einfügen"
            >
              <FiPlus />
            </button>
          )}

          {/* Sort Button */}
          {bars.length > 1 && (
            <button
              onClick={sortBars}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#5a6268';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#6c757d';
                e.target.style.transform = 'scale(1)';
              }}
              title="Nachweise manuell chronologisch sortieren (nach Start-Zeit)"
            >
              ⟷
            </button>
          )}
          </div>
        </div>
      )}



      {/* Movable bars */}
      {barsVisible && (
        <div>
          {bars
          .map(bar => {
            if (bar.startDate && bar.endDate) {
              const recalculatedPosition = calculateBarPosition(bar.startDate, bar.endDate);
              return {
                ...bar,
                x: recalculatedPosition.x,
                width: recalculatedPosition.width
              };
            }
            return bar;
          })
          .filter(bar => bar.width > 0 && bar.x > -100) // Nur sichtbare Bars rendern
          .map((bar, index, filteredBars) => {
          
          // Sortiere die Bars nach Y-Position um zu bestimmen welches das erste/letzte ist
          const sortedBars = [...filteredBars].sort((a, b) => a.y - b.y);
          const sortedIndex = sortedBars.findIndex(b => b.id === bar.id);
          const isFirst = sortedIndex === 0;
          const isLast = sortedIndex === sortedBars.length - 1;
          
          return (
            <MovableBar
              key={bar.uuid || `bar-${bar.originalId || bar.id}`} // Stabiler Key verhindert unnötige Remounts
              id={bar.id}
              uuid={bar.uuid}
              initialX={bar.x}
              initialY={bar.y}
              initialWidth={bar.width}
              initialLabel={bar.label}
              startDate={bar.startDate}
              endDate={bar.endDate}
              textColor={bar.textColor || '#ffffff'}
              backgroundColor={bar.backgroundColor || '#007bff'}
              borderStyle={bar.borderStyle || 'solid'}
              borderColor={bar.borderColor || '#0056b3'}
              borderThickness={bar.borderThickness || 1}
              linkEvidence={bar.linkEvidence || false}
              evidenceType={bar.evidenceType || 'moodle-badge'}
              evidenceLabel={bar.evidenceLabel || ''}
              evidenceDueDays={bar.evidenceDueDays || 30}
              evidenceData={bar.evidenceData || {}}
              learnerBadges={bar.learnerBadges || []}
              onDelete={deleteBar}
              onUpdate={updateBar}
              onEdit={handleEditBar}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragMove={handleDragMove}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              isEditing={showEditButtons}
              isFirst={isFirst}
              isLast={isLast}
              visibleWeeks={visibleWeeks}
              weekWidth={weekWidth}
              hasEditPermission={hasEditPermission}
              showInfoPanel={openInfoPanels.includes(bar.id)}
              onToggleInfoPanel={() => handleToggleInfoPanel(bar.id)}
              user={user}
              showSelection={batchOperations ? batchOperations.isSelectionMode : false}
              isSelected={batchOperations ? batchOperations.isTaskSelected(`${panelId}_${bar.id}`) : false}
              onSelectionChange={batchOperations ? (taskId, isSelected) => batchOperations.toggleTaskSelection(`${panelId}_${taskId}`, isSelected) : null}
              panelId={panelId}
              panelTitle={title || 'Unbenanntes Panel'}
              panelGroupTitle={groupTitle || 'Unbenannte Gruppe'}
              assignedStudents={bar.assignedStudents || []}
            />
          );
        })}
        </div>
      )}

      {/* Info Panels für alle offenen Panels - nur sichtbar wenn Bars sichtbar sind */}
      {barsVisible && (
        <div>
          {user && openInfoPanels.map(barId => {
          const bar = bars.find(b => b.id === barId);
          if (!bar) return null;
          
          return (
            <div
              key={`info-panel-${barId}`}
              style={{
                position: 'absolute',
                top: `${bar.y + 28 + 2}px`, // Direkt unterhalb der Bar
                left: `${bar.x}%`,
                width: `${bar.width}%`,
                zIndex: 100
              }}
            >
              <TaskInfoPanel 
                isVisible={true}
                onClose={() => handleCloseInfoPanel(barId)}
                barId={barId}
                barLabel={bar.label}
                onOpenSchuelerModal={handleOpenSchuelerModal}
                onHeightChange={handleInfoPanelHeightChange}
                assignedStudents={bar.assignedStudents || []}
                onStudentsUpdate={handleStudentsUpdate}
                onTaskDataUpdate={handleTaskDataUpdate}
                user={user} // User-Objekt für Lernenden-Erkennung weiterleiten
                taskData={{
                  ...bar,
                  startDate: bar.startDate,
                  endDate: bar.endDate,
                  evidenceType: bar.evidenceType,
                  evidenceData: bar.evidenceData,
                  linkEvidence: bar.linkEvidence,
                  visibleWeeks: visibleWeeks,
                  weekWidth: weekWidth,
                  viewStartDate: startDate, // StartDate-Context für korrekte Badge-Positionierung
                  taskXPosition: bar.x, // X-Position des MovableBar im Container
                  taskWidth: bar.width // Breite des MovableBar
                }}
              />
            </div>
          );
        })}
        </div>
      )}

      {/* SchuelerModal - auf oberster Ebene gerendert */}
      <SchuelerModal
        show={showSchuelerModal}
        onClose={handleCloseSchuelerModal}
        onUebernehmen={handleUebernehmenLernende}
        existingStudents={currentBarIdForModal ? (bars.find(bar => bar.id === currentBarIdForModal)?.assignedStudents || []) : []}
        currentTaskData={currentBarIdForModal ? bars.find(bar => bar.id === currentBarIdForModal) : null}
      />
    </div>
  );
});

export default Task;
